import { useProviderHub, useTokenInfoHub } from '@aries/aptos-defi/common';
import { getClaimSDK } from '@aries/aptos-defi/common/aries-sdk';
import {
  APTOS_COIN_ADDRESS,
  ARIES_BORROW_APT_CLAIM_ADDRSS_MAINNET,
  ARIES_BORROW_USDC_CLAIM_ADDRESS_MAINNET,
  ARIES_PROGRAM_ADDRESS,
  MAINNET_USDC_WCOIN,
} from '@aries/aptos-defi/config';
import { useWallet } from '@aries/aptos-defi/wallet';
import Big from 'big.js';
import { compact, concat } from 'lodash';
import { useMemo } from 'react';
import useSWR from 'swr';
import { useLendingMarkets } from '../market';
import { claimBorrowRebate } from './claim-borrow-rebate';

export const useUserBorrowRebate = () => {
  const aptBorrowRebates = useUserBorrowRebateByToken(
    APTOS_COIN_ADDRESS,
    ARIES_BORROW_APT_CLAIM_ADDRSS_MAINNET,
  );

  const usdcBorrowRebates = useUserBorrowRebateByToken(
    MAINNET_USDC_WCOIN,
    ARIES_BORROW_USDC_CLAIM_ADDRESS_MAINNET,
  );

  return useMemo(() => {
    return compact(concat(aptBorrowRebates, usdcBorrowRebates));
  }, [aptBorrowRebates, usdcBorrowRebates]);
};

export const useUserBorrowRebateByToken = (
  rebateTokenAddress: string,
  rebateContractAddress: string,
) => {
  const { walletAddress } = useWallet();
  const {
    provider: { client: aptosClient },
    env,
  } = useProviderHub();

  const { activeMarketId } = useLendingMarkets();

  const { tokenMap } = useTokenInfoHub();
  const tokenToClaim = tokenMap[rebateTokenAddress];

  const isMainPool = ARIES_PROGRAM_ADDRESS === activeMarketId;

  const { data: userClaimStats, mutate } = useSWR(
    [
      'ARSUserRebaseClaimStats',
      walletAddress,
      env.isMainnet,
      rebateTokenAddress,
      rebateContractAddress,
    ],
    async () => {
      if (!walletAddress || !env.isMainnet) {
        return undefined;
      }

      const getUserStats = await getClaimSDK(rebateContractAddress)
        .claim.getUserStats({
          user_addr: walletAddress,
        })
        .makePayload();

      const [remainingBalance, claimed] = (await aptosClient.view(
        getUserStats,
      )) as any[];

      return {
        remainingBalance: Big(remainingBalance),
        claimed: Big(claimed),
      };
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      errorRetryCount: 0,
    },
  );

  return useMemo(() => {
    return isMainPool
      ? [
          {
            asset: tokenToClaim,
            claimable: tokenToClaim.lamportsToBalance(
              userClaimStats?.remainingBalance || Big(0),
            ),
            refreshClaimable: async () => {
              await mutate();
            },
            claimBorrowRebate: () =>
              claimBorrowRebate(rebateContractAddress),
          },
        ].filter(_rebate => _rebate.claimable.amountBig.gt(0))
      : undefined;
  }, [
    isMainPool,
    mutate,
    rebateContractAddress,
    tokenToClaim,
    userClaimStats?.remainingBalance,
  ]);
};
